'use client'

import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffEventInput, StaffEventType, StaffEventWithWorker } from '@garage/shared'
import { staffEventInputSchema, STAFF_EVENT_TYPES, STAFF_EVENT_TYPE_LABELS, computeLateDeduction, computeOvertimePay } from '@garage/shared'
import { Dialog, Segmented, Select, TextField, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { useSettings, useFormatters } from '@/lib/hooks.js'

interface StaffEventDialogProps {
  open: boolean
  /** Prefilled date for a new event — the day the user clicked. */
  date: string
  event?: StaffEventWithWorker
  onClose: () => void
  /** Called after a successful save/delete so the calendar can refetch its range. */
  onSaved: () => void
}

interface FormState {
  workerId: string
  type: StaffEventType
  date: string
  endDate: string
  note: string | null
  arrivalTime: string | null
  departureTime: string | null
}

export function StaffEventDialog({ open, date, event, onClose, onSaved }: StaffEventDialogProps) {
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const { data: settings } = useSettings()
  const formatters = useFormatters()

  const isCreate = !event

  const { data: workersData } = useQuery({
    queryKey: ['workers-for-event-picker'],
    queryFn: () => api.workers.list(),
  })

  const initialState = (): FormState => ({
    workerId: event?.workerId ?? '',
    type: event?.type ?? 'late',
    date: event?.date ?? date,
    endDate: event?.endDate ?? date,
    note: event?.note ?? null,
    arrivalTime: event?.arrivalTime ?? null,
    departureTime: event?.departureTime ?? null,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event, date])

  const saveMutation = useMutation({
    mutationFn: async (input: StaffEventInput) =>
      isCreate ? api.staffEvents.create(input) : api.staffEvents.update(event!.id, input),
    onSuccess: () => {
      snackbar.success(isCreate ? t('calendar.eventAdded') : t('calendar.eventUpdated'))
      onSaved()
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.staffEvents.remove(event!.id),
    onSuccess: () => {
      snackbar.success(t('calendar.eventRemoved'))
      onSaved()
      onClose()
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('calendar.failedToRemoveEvent'))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input: StaffEventInput = {
      workerId: formState.workerId || null,
      type: formState.type,
      date: formState.date,
      endDate: formState.type === 'holiday' ? formState.endDate : formState.date,
      note: formState.note,
      arrivalTime: formState.type === 'late' ? formState.arrivalTime : null,
      departureTime: formState.type === 'overtime' ? formState.departureTime : null,
    }

    const result = staffEventInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    saveMutation.mutate(input)
  }

  const workerOptions = [
    ...(workersData?.items.map((w) => ({ value: w.id, label: w.name })) ?? []),
  ]

  const selectedWorker = workersData?.items.find((w) => w.id === formState.workerId)
  const deductionPreview =
    formState.type === 'late' && settings?.latePolicyEnabled && formState.arrivalTime && selectedWorker
      ? computeLateDeduction(formState.arrivalTime, selectedWorker.monthlySalaryMinor, settings)
      : null
  const overtimePreview =
    formState.type === 'overtime' && settings?.overtimePolicyEnabled && formState.departureTime && selectedWorker
      ? computeOvertimePay(formState.departureTime, selectedWorker.monthlySalaryMinor, settings)
      : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('calendar.addEvent') : t('calendar.editEvent')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-between w-full">
          {!isCreate ? (
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
              disabled={saveMutation.isPending}
            >
              {t('common.delete')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <Button variant="outlined" onClick={onClose} disabled={saveMutation.isPending || deleteMutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button variant="filled" onClick={handleSubmit} loading={saveMutation.isPending}>
              {isCreate ? t('common.add') : t('common.save')}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Segmented
          label={t('calendar.whatHappened')}
          wrap
          options={STAFF_EVENT_TYPES.map((type) => ({
            value: type,
            label: t(`calendar.eventTypes.${type}`, STAFF_EVENT_TYPE_LABELS[type]),
          }))}
          value={formState.type}
          onChange={(type) => setFormState((s) => ({ ...s, type: type as StaffEventType }))}
        />

        <Select
          label={formState.type === 'holiday' ? t('calendar.worker') : t('calendar.workerOptional')}
          value={formState.workerId}
          onChange={(v) => setFormState((s) => ({ ...s, workerId: v }))}
          options={workerOptions}
          placeholder={formState.type === 'holiday' ? t('calendar.whoIsOnLeave') : t('calendar.generalNotAboutWorker')}
          error={fieldErrors.workerId}
          fullWidth
        />

        <div className={formState.type === 'holiday' || formState.type === 'late' || formState.type === 'overtime' ? 'grid grid-cols-2 gap-3' : ''}>
          <TextField
            label={formState.type === 'holiday' ? t('calendar.firstDay') : t('calendar.date')}
            type="date"
            value={formState.date}
            onChange={(v) =>
              setFormState((s) => ({ ...s, date: v, endDate: s.endDate < v ? v : s.endDate }))
            }
            error={fieldErrors.date}
            fullWidth
          />

          {formState.type === 'holiday' && (
            <TextField
              label={t('calendar.lastDay')}
              type="date"
              value={formState.endDate}
              onChange={(v) => setFormState((s) => ({ ...s, endDate: v }))}
              error={fieldErrors.endDate}
              fullWidth
            />
          )}

          {formState.type === 'late' && (
            <TextField
              label={t('calendar.arrivalTime')}
              type="time"
              value={formState.arrivalTime ?? ''}
              onChange={(v) => setFormState((s) => ({ ...s, arrivalTime: v || null }))}
              error={fieldErrors.arrivalTime}
              fullWidth
            />
          )}

          {formState.type === 'overtime' && (
            <TextField
              label={t('calendar.departureTime')}
              type="time"
              value={formState.departureTime ?? ''}
              onChange={(v) => setFormState((s) => ({ ...s, departureTime: v || null }))}
              error={fieldErrors.departureTime}
              fullWidth
            />
          )}
        </div>

        {formState.type === 'late' && settings?.latePolicyEnabled && formState.arrivalTime && (
          <div className="rounded-md border border-outline-variant bg-surface-high px-3 py-2 text-body-sm">
            {!selectedWorker ? (
              <span className="text-on-surface-variant">{t('calendar.deductionNeedsWorker')}</span>
            ) : deductionPreview ? (
              <span className="text-on-surface">
                {t('calendar.deductionPreview', {
                  minutes: deductionPreview.minutesLate,
                  amount: formatters.money(deductionPreview.deductionMinor),
                })}
              </span>
            ) : selectedWorker.monthlySalaryMinor ? (
              <span className="text-on-surface-variant">{t('calendar.withinBuffer')}</span>
            ) : (
              <span className="text-on-surface-variant">{t('calendar.deductionNeedsSalary')}</span>
            )}
          </div>
        )}

        {formState.type === 'overtime' && settings && !settings.overtimePolicyEnabled && (
          <div className="rounded-md border border-warning bg-surface-high px-3 py-2 text-body-sm text-on-surface">
            {t('calendar.overtimePolicyOff')}
          </div>
        )}

        {formState.type === 'overtime' && settings?.overtimePolicyEnabled && formState.departureTime && (
          <div className="rounded-md border border-outline-variant bg-surface-high px-3 py-2 text-body-sm">
            {!selectedWorker ? (
              <span className="text-on-surface-variant">{t('calendar.overtimeNeedsWorker')}</span>
            ) : overtimePreview ? (
              <span className="text-on-surface">
                {t('calendar.overtimePreview', {
                  minutes: overtimePreview.minutesOver,
                  amount: formatters.money(overtimePreview.payMinor),
                })}
              </span>
            ) : selectedWorker.monthlySalaryMinor ? (
              <span className="text-on-surface-variant">{t('calendar.overtimeWithinBuffer')}</span>
            ) : (
              <span className="text-on-surface-variant">{t('calendar.overtimeNeedsSalary')}</span>
            )}
          </div>
        )}

        <TextField
          label={formState.type === 'other' ? t('calendar.whatHappened') : t('calendar.notesOptional')}
          value={formState.note ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, note: v || null }))}
          error={fieldErrors.note}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
